// ============================================================
// PERRY — THE ASCENSION (the Sheathe's screen)
//
// caP'n, 2026-07-30: "the Sheathe explains he must upgrade his
// weapon or even find new ones and Ascend them... so we will
// actually rework some stuff so Perry never actually gets Imperas
// or an U'hlti or anything extravagant — all the arts go to the
// swords, that's the restructure."
//
// This is that screen. It is the only place in the title where a
// weapon becomes more than the row it shipped as, and it is the
// only place the Sheathe and the blade are both on stage.
//
// WHY IT IS ITS OWN FILE AND NOT A FIFTH TAB IN Customize.
// The constellation, the Grimoire, the Armoury and the Wares are
// all lists of things you own. This is a LADDER for one object at
// a time, with a character talking over it, and it wants the whole
// width. Cramming it into the tab strip would have made the most
// important screen in the game the smallest one.
//
// IT WRITES NOTHING. Every action is a callback up to App, which
// owns the Firebase layer exactly like every other screen — the
// architecture default this project has never once broken. What
// this file owns is the ladder's shape, the Sheathe's timing, and
// the one piece of arithmetic nobody else should be doing:
// whether a rung is legal to buy right now.
// ============================================================

import { useState, useEffect, useMemo } from "react";
import { GM } from "./theme";
import { MetalRule, ensureKeyframes } from "./PerryUI";
import {
  TREASURES, GLYPH, RULES_ARM, ARM_LADDER, ARM_TREES, SHEATHE,
  ownsTreasure, armProgress, deriveArm, deriveTreasure, sheatheSays,
  TRACKS,
} from "./perryData";

const KF = `
@keyframes ascIn    { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }
@keyframes ascGlow  { 0%,100% { box-shadow: 0 0 14px rgba(201,162,39,0.20); } 50% { box-shadow: 0 0 26px rgba(201,162,39,0.55); } }
@keyframes ascSpeak { from { opacity: 0; transform: translateX(-8px); } to { opacity: 1; transform: translateX(0); } }
@keyframes ascLord  { 0%,100% { filter: brightness(1); } 50% { filter: brightness(1.35); } }
`;

// Who is talking, and what that looks like. A map, not a ternary
// chain — a fourth speaker is a row in SHEATHE_LINES and a row
// here, and nothing else in the file changes.
const VOICE = {
  sheathe: { tint: "#ffe6a8", label: "THE SHEATHE" },
  blade: { tint: "#b8c4cc", label: "THE BLADE" },
  world: { tint: "#8a8078", label: null },
};

// ── THE SHEATHE SPEAKING ─────────────────────────────────────
// Beats arrive one at a time on a timer, because an exchange that
// lands all at once is a paragraph and an exchange that lands in
// order is a conversation. That is the entire difference between
// this reading as dialogue and reading as a patch note.
function Speech({ beats }) {
  const [shown, setShown] = useState(0);
  useEffect(() => {
    setShown(0);
    if (!beats?.length) return undefined;
    const timers = beats.map((_, i) =>
      setTimeout(() => setShown((n) => Math.max(n, i + 1)), 260 + i * 900)
    );
    return () => timers.forEach(clearTimeout);
  }, [beats]);

  if (!beats?.length) return null;
  return (
    <div style={{
      marginTop: 14, padding: "12px 16px", borderRadius: 10,
      background: "linear-gradient(175deg, #17110a, #0c0806)",
      border: `1px solid ${GM.inkGold}44`,
      minHeight: 54,
    }}>
      {beats.slice(0, shown).map((b, i) => {
        const v = VOICE[b.who] || VOICE.world;
        return (
          <div key={i} style={{ marginBottom: 7, animation: "ascSpeak 0.42s ease-out" }}>
            {v.label && (
              <div style={{
                fontSize: 8, letterSpacing: 3, color: `${v.tint}88`, marginBottom: 2,
              }}>{v.label}</div>
            )}
            <div style={{
              fontSize: 12.5, lineHeight: 1.55, color: v.tint,
              fontStyle: b.who === "world" ? "italic" : "normal",
              textAlign: b.who === "world" ? "center" : "left",
              opacity: b.who === "world" ? 0.75 : 1,
            }}>
              {b.who === "world" ? b.text : `“${b.text}”`}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── ONE RUNG ─────────────────────────────────────────────────
// The pip, the price, and what it actually gives. The COPY LAW
// applies here as hard as it applies anywhere: the grant leads,
// the flavour follows, and the design rationale stays in the data
// file's comments where a player will never trip over it.
function Rung({ rung, grant, state, onBuy, kindling, locked }) {
  const lit = !!state;
  const afford = kindling >= rung.cost;
  const can = !lit && !locked && afford;

  // The grant, said flatly, in the smallest number of words that
  // are still true. Built from the data — never hand-written twice.
  const bits = [];
  if (grant?.lyt) bits.push(`+${grant.lyt} LYT`);
  if (grant?.lyf) bits.push(`+${grant.lyf} LYF`);
  if (grant?.sight) bits.push(`+${grant.sight} COAL NAMED`);
  if (grant?.pierce) bits.push(`+${grant.pierce} PIERCE`);
  if (grant?.ladle) bits.push(`+${grant.ladle} MERCY`);
  if (grant?.windowMod) bits.push(`${grant.windowMod > 0 ? "+" : ""}${Math.round(grant.windowMod * 100)}% WINDOWS`);
  if (grant?.grip) bits.push(`${grant.grip > 0 ? "+" : ""}${Math.round(grant.grip * 100)}% HOLDS`);
  for (const [t, n] of Object.entries(grant?.ranks || {})) {
    bits.push(`+${n} ${(TRACKS[t]?.name || t).toUpperCase()}`);
  }
  if (grant?.regalia) bits.push("LEARNS AN ART");

  return (
    <div style={{
      display: "flex", gap: 12, alignItems: "flex-start",
      padding: "11px 13px", borderRadius: 10, marginBottom: 8,
      background: lit
        ? "linear-gradient(175deg, #3a2810, #1a1008)"
        : "linear-gradient(175deg, #14100b, #0b0806)",
      border: `1px solid ${lit ? GM.inkGold : GM.pageBorder}`,
      opacity: locked && !lit ? 0.42 : 1,
      transition: "opacity 0.2s",
    }}>
      <div style={{
        width: 34, height: 34, flexShrink: 0, borderRadius: 17,
        display: "flex", alignItems: "center", justifyContent: "center",
        background: lit ? "radial-gradient(circle, #ffd88a, #8a5a18)" : "#100b07",
        border: `1px solid ${lit ? "#ffe6a8" : GM.pageBorder}`,
        color: lit ? "#2a1808" : GM.vellumDim,
        fontFamily: GM.font, fontSize: 12, letterSpacing: 1,
        animation: lit ? "ascGlow 3.4s ease-in-out infinite" : "none",
      }}>{rung.numeral}</div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "baseline" }}>
          <div style={{ fontSize: 11, letterSpacing: 2, color: lit ? "#ffe6c2" : GM.vellum }}>
            {bits.length ? bits.join(" · ") : "—"}
          </div>
          <div style={{ fontSize: 9, letterSpacing: 2, color: GM.vellumFaint, whiteSpace: "nowrap" }}>
            LEVEL {rung.level}
          </div>
        </div>
        {grant?.note && (
          <div style={{ fontSize: 11, lineHeight: 1.5, color: GM.vellumFaint, marginTop: 4, fontStyle: "italic" }}>
            {grant.note}
          </div>
        )}
      </div>

      <div style={{ flexShrink: 0 }}>
        {lit ? (
          <span style={{ fontSize: 10, letterSpacing: 2, color: GM.inkGoldBright }}>LIT</span>
        ) : (
          <button
            onClick={() => can && onBuy(rung)}
            style={{
              padding: "6px 12px", borderRadius: 7,
              cursor: can ? "pointer" : "not-allowed",
              background: can ? "linear-gradient(175deg, #7a4218, #3a2010)" : "#140d08",
              border: `1px solid ${can ? GM.inkGold : GM.pageBorder}`,
              color: can ? "#ffe6c2" : GM.vellumFaint,
              fontFamily: GM.fontUI, fontSize: 10, letterSpacing: 2,
              whiteSpace: "nowrap",
            }}
          >{GLYPH.kd} {rung.cost}</button>
        )}
      </div>
    </div>
  );
}

export default function Ascension({
  soul, kindling = 0, onAscendArm, onReforge, onAwaken, onBack,
}) {
  ensureKeyframes("perry-ascension-keyframes", KF);
  const held = deriveTreasure(soul);
  const [sel, setSel] = useState(held.id);
  const [beats, setBeats] = useState(null);
  const [busy, setBusy] = useState(false);

  // Everything the player OWNS, in registry order so the shelf
  // never reshuffles itself between visits. The reed is always
  // there — a beb who owns nothing still walked out of Reedbrook
  // holding something.
  const owned = useMemo(
    () => Object.keys(TREASURES).filter((id) => ownsTreasure(soul, id)),
    [soul]
  );

  const prog = armProgress(soul, sel);
  const arm = deriveArm(soul, sel);
  const tree = ARM_TREES[sel];
  const row = TREASURES[sel];
  const isHeld = held.id === sel;
  const awakening = prog.awakening;

  const speak = (at) => setBeats(sheatheSays(at));

  const buy = async (rung) => {
    if (busy) return;
    setBusy(true);
    const ok = await onAscendArm(sel, rung.key, rung.cost);
    setBusy(false);
    if (!ok) return;
    // The crown speaks louder than a rung, and it only gets to say
    // so once — check AFTER the write, against what the write made
    // true, never against what we hoped it would.
    const after = ARM_LADDER.filter((r) => r.key === rung.key || soul?.arms?.[sel]?.rungs?.[r.key]);
    speak(after.length >= ARM_LADDER.length ? "crown" : "ascend");
  };

  const reforge = async () => {
    if (busy || isHeld) return;
    setBusy(true);
    const ok = await onReforge(sel, RULES_ARM.REFORGE_COST);
    setBusy(false);
    if (ok) speak("reforge");
  };

  const awaken = async () => {
    if (busy) return;
    setBusy(true);
    const ok = await onAwaken(sel);
    setBusy(false);
    if (ok) speak("awaken");
  };

  return (
    <div style={{
      minHeight: "100vh", padding: "26px 14px 44px",
      display: "flex", flexDirection: "column", alignItems: "center",
      background: "radial-gradient(ellipse at 50% 0%, #1a120a 0%, #080505 70%)",
    }}>
      <div style={{ animation: "ascIn 0.5s ease-out", width: "min(760px, 96vw)" }}>

        {/* ── THE SHEATHE'S HEADER ─────────────────────────── */}
        <div style={{ textAlign: "center", marginBottom: 6 }}>
          <div style={{
            fontFamily: GM.font, fontSize: 22, letterSpacing: 5, color: GM.inkGoldBright,
          }}>THE ASCENSION</div>
          <div style={{ fontSize: 11, color: GM.vellumFaint, letterSpacing: 2, marginTop: 4 }}>
            {SHEATHE.name.toUpperCase()} — {SHEATHE.what}
          </div>
        </div>
        <MetalRule />

        <div style={{
          display: "flex", justifyContent: "space-between", alignItems: "center",
          margin: "12px 0 14px", flexWrap: "wrap", gap: 8,
        }}>
          <button onClick={onBack} style={{
            padding: "6px 14px", borderRadius: 7, cursor: "pointer",
            background: "linear-gradient(175deg, #1c1009, #100906)",
            border: `1px solid ${GM.pageBorder}`, color: GM.vellumDim,
            fontFamily: GM.fontUI, fontSize: 10, letterSpacing: 3,
          }}>← BACK</button>
          <div style={{ fontSize: 12, letterSpacing: 2, color: GM.inkGoldBright }}>
            {GLYPH.kd} {kindling}
          </div>
        </div>

        {/* ── WHICH OBJECT ─────────────────────────────────── */}
        <div style={{ display: "flex", gap: 7, flexWrap: "wrap", marginBottom: 14 }}>
          {owned.map((id) => {
            const t = TREASURES[id];
            const p = armProgress(soul, id);
            const on = id === sel;
            return (
              <button key={id} onClick={() => { setSel(id); setBeats(null); }} style={{
                padding: "6px 11px", borderRadius: 8, cursor: "pointer",
                background: on ? "linear-gradient(175deg, #7a4218, #3a2010)" : "linear-gradient(175deg, #1c1009, #100906)",
                border: `1px solid ${p.lord ? "#ffe6a8" : on ? GM.inkGold : GM.pageBorder}`,
                color: on ? "#ffe6c2" : GM.vellumDim,
                fontFamily: GM.fontUI, fontSize: 11, letterSpacing: 1,
                animation: p.lord ? "ascLord 3s ease-in-out infinite" : "none",
              }}>
                {t.icon} {t.name}
                <span style={{ marginLeft: 6, fontSize: 9, opacity: 0.75 }}>
                  {p.lord ? "LORD" : `${p.lit}/${p.of}`}
                </span>
              </button>
            );
          })}
        </div>

        {/* ── THE OBJECT ITSELF ────────────────────────────── */}
        <div style={{
          padding: "14px 17px", borderRadius: 12, marginBottom: 14,
          background: "linear-gradient(175deg, #1c1009, #0d0705)",
          border: `1px solid ${prog.lord ? "#ffe6a8" : GM.pageBorder}`,
        }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "baseline", flexWrap: "wrap" }}>
            <div style={{ fontFamily: GM.font, fontSize: 16, letterSpacing: 2, color: GM.vellum }}>
              {row.icon} {arm.title || row.name}
            </div>
            <div style={{ fontSize: 10, letterSpacing: 2, color: GM.vellumFaint }}>
              {isHeld ? "IN YOUR PAWS" : "ON THE SHELF"}
              {prog.lord ? " · LORD" : prog.level ? ` · LEVEL ${prog.level}` : ""}
            </div>
          </div>
          {tree?.name && (
            <div style={{ fontSize: 10, letterSpacing: 3, color: GM.inkGoldBright, marginTop: 5 }}>
              {tree.name}
            </div>
          )}

          {/* ── WHAT IT IS WORTH RIGHT NOW ──
              Read off deriveArm, never off the shelf row, so an
              Ascended weapon and a stripped one tell the truth
              about themselves without this file knowing why. */}
          <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginTop: 10, fontSize: 11, color: GM.vellumFaint }}>
            <span>LYT <b style={{ color: GM.vellum }}>{arm.lyt || 0}</b></span>
            {!!arm.lyf && <span>LYF <b style={{ color: GM.vellum }}>+{arm.lyf}</b></span>}
            {!!arm.pierce && <span>PIERCE <b style={{ color: GM.vellum }}>{arm.pierce}</b></span>}
            {!!arm.sight && <span>SIGHT <b style={{ color: GM.vellum }}>{arm.sight}</b></span>}
            <span>WINDOWS <b style={{ color: (arm.windowMod || 0) < 0 ? "#f4726a" : GM.vellum }}>
              {(arm.windowMod || 0) >= 0 ? "+" : ""}{Math.round((arm.windowMod || 0) * 100)}%
            </b></span>
            {!!(arm.regalia || []).length && (
              <span>ARTS <b style={{ color: GM.inkGoldBright }}>{arm.regalia.length}</b></span>
            )}
          </div>

          {/* ── THE WEIGHT NOTE ──
              The one number a player could never see before and
              which cost them two levels of Grace in silence. It is
              stated here, on the object, in plain words. */}
          {(row.windowMod || 0) < 0 && (
            <div style={{ fontSize: 11, lineHeight: 1.5, color: GM.vellumFaint, marginTop: 8 }}>
              This thing is <b style={{ color: "#f4726a" }}>heavy</b> — it narrows the moment your answer
              counts. <b style={{ color: GM.inkGoldBright }}>Read the Flame</b> forgives that weight, a
              third per level, and forgives all of it at level 3.
            </div>
          )}
        </div>

        {/* ── THE AWAKENING, IF IT IS RUNNING ──────────────── */}
        {awakening && (
          <div style={{
            padding: "13px 16px", borderRadius: 11, marginBottom: 14,
            background: "linear-gradient(175deg, #2a1008, #140806)",
            border: "1px solid #8e3a30",
          }}>
            <div style={{ fontSize: 11, letterSpacing: 3, color: "#f4726a", marginBottom: 5 }}>
              THE AWAKENING
            </div>
            <div style={{ fontSize: 12, lineHeight: 1.55, color: GM.vellum }}>
              It is a stick again. Everything it learned is still in there and none of it is
              reaching your paws until this is done. Break things with it.
            </div>
            <div style={{ fontSize: 13, letterSpacing: 2, color: GM.inkGoldBright, marginTop: 8 }}>
              {awakening.ruins || 0} / {RULES_ARM.AWAKEN_RUINS} RUINED
            </div>
          </div>
        )}

        {/* ── THE LADDER ───────────────────────────────────── */}
        {tree ? (
          <>
            {ARM_LADDER.map((rung, i) => {
              // Locked until the rung below it is lit. One rule, read
              // off the ladder itself — nothing hardcodes an order.
              const prev = i === 0 ? true : !!soul?.arms?.[sel]?.rungs?.[ARM_LADDER[i - 1].key];
              return (
                <Rung
                  key={rung.key}
                  rung={rung}
                  grant={tree[rung.key]}
                  state={!!soul?.arms?.[sel]?.rungs?.[rung.key]}
                  onBuy={buy}
                  kindling={kindling}
                  locked={!prev || !!awakening}
                />
              );
            })}

            {/* ── PAST THE LADDER ──
                Lord is not a fourth level. It is what happens after
                the third one is spent, and it cannot be bought. */}
            <div style={{
              marginTop: 6, padding: "13px 16px", borderRadius: 11,
              background: prog.lord
                ? "linear-gradient(175deg, #4a3410, #1a1008)"
                : "linear-gradient(175deg, #14100b, #0b0806)",
              border: `1px solid ${prog.lord ? "#ffe6a8" : GM.pageBorder}`,
              opacity: prog.crowned || prog.lord ? 1 : 0.45,
              animation: prog.lord ? "ascLord 3s ease-in-out infinite" : "none",
            }}>
              <div style={{ fontSize: 11, letterSpacing: 4, color: prog.lord ? "#ffe6a8" : GM.vellumDim }}>
                {prog.lord ? `LORD — ${tree.lord?.title || ""}` : "LORD"}
              </div>
              <div style={{ fontSize: 11.5, lineHeight: 1.55, color: GM.vellumFaint, marginTop: 6 }}>
                {prog.lord
                  ? tree.lord?.note
                  : prog.crowned
                    ? "There is nothing left on it that the Sheathe knows how to give. Everything after this it has to go and take."
                    : "Light every rung first. Then it stops being a shopping list and starts being a mission."}
              </div>
              {prog.crowned && !prog.lord && !awakening && (
                <button onClick={awaken} style={{
                  marginTop: 10, padding: "8px 16px", borderRadius: 8, cursor: "pointer",
                  background: "linear-gradient(175deg, #7a4218, #3a2010)",
                  border: `1px solid ${GM.inkGold}`, color: "#ffe6c2",
                  fontFamily: GM.fontUI, fontSize: 11, letterSpacing: 3,
                }}>BEGIN THE AWAKENING</button>
              )}
            </div>
          </>
        ) : (
          <div style={{
            padding: "16px", borderRadius: 11, textAlign: "center",
            border: `1px solid ${GM.pageBorder}`, color: GM.vellumFaint, fontSize: 12,
          }}>
            The Sheathe looks at this one for a long moment and says nothing at all.
          </div>
        )}

        {/* ── REFORGE ──────────────────────────────────────── */}
        {!isHeld && (
          <div style={{
            marginTop: 14, padding: "12px 16px", borderRadius: 11,
            background: "linear-gradient(175deg, #14100b, #0b0806)",
            border: `1px solid ${GM.pageBorder}`,
          }}>
            <div style={{ fontSize: 11.5, lineHeight: 1.55, color: GM.vellumFaint }}>
              Move the Sheathe's attention to this one. Everything lit on what you are
              carrying <b style={{ color: GM.vellum }}>stays lit</b> — it waits on the shelf for you.
              This is a fee, not a reset.
            </div>
            <button onClick={reforge} disabled={kindling < RULES_ARM.REFORGE_COST} style={{
              marginTop: 9, padding: "7px 15px", borderRadius: 8,
              cursor: kindling >= RULES_ARM.REFORGE_COST ? "pointer" : "not-allowed",
              background: kindling >= RULES_ARM.REFORGE_COST
                ? "linear-gradient(175deg, #7a4218, #3a2010)" : "#140d08",
              border: `1px solid ${kindling >= RULES_ARM.REFORGE_COST ? GM.inkGold : GM.pageBorder}`,
              color: kindling >= RULES_ARM.REFORGE_COST ? "#ffe6c2" : GM.vellumFaint,
              fontFamily: GM.fontUI, fontSize: 10, letterSpacing: 2,
            }}>TAKE IT UP — {GLYPH.kd} {RULES_ARM.REFORGE_COST}</button>
          </div>
        )}

        <Speech beats={beats} />
      </div>
    </div>
  );
}
